import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, money } from "@/lib/api";
import { listFiles, deleteFile, humanBytes, fileBlobUrl } from "@/lib/files";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, CheckCircle2, Circle, Clock, GripVertical, Loader2,
  Folder, FolderOpen, Download, Trash2, AlertTriangle, ChevronRight, MessageSquare,
  Columns3, GanttChartSquare, Repeat, Eye,
} from "lucide-react";
import { cn } from "@/lib/utils";
import FileDropZone from "@/components/FileDropZone";
import TaskDrawer from "@/components/TaskDrawer";
import ProjectTimeline from "@/components/ProjectTimeline";
import AssigneeAvatar from "@/components/AssigneeAvatar";
import SubtaskChecklist from "@/components/SubtaskChecklist";
import FilePreviewDialog from "@/components/FilePreviewDialog";

const PRIORITY_STYLE = {
  urgent: "bg-[hsl(var(--studio-danger)/0.12)] text-[hsl(var(--studio-danger))] border-[hsl(var(--studio-danger)/0.25)]",
  high: "bg-[hsl(var(--studio-warning)/0.22)] text-[hsl(34_42%_34%)] dark:text-[hsl(34_50%_78%)] border-[hsl(var(--studio-warning)/0.35)]",
  normal: "bg-muted text-muted-foreground border-transparent",
  low: "bg-muted/60 text-muted-foreground border-transparent",
};

const STAGE_STATUS_STYLE = {
  active: "bg-[hsl(var(--studio-primary)/0.08)] border-[hsl(var(--studio-primary)/0.35)]",
  done: "bg-[hsl(var(--halo-teal)/0.06)] border-[hsl(var(--halo-teal)/0.30)]",
  todo: "bg-card border-border",
};

export default function ProjectDetail() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragTaskId, setDragTaskId] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [pendingMove, setPendingMove] = useState(null); // {task, fromStage, toStage}
  const [openFolder, setOpenFolder] = useState(null);
  const [openTaskId, setOpenTaskId] = useState(null);
  const [view, setView] = useState("kanban");
  const [people, setPeople] = useState([]);
  const [previewFile, setPreviewFile] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [p, t, d, f] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get(`/tasks?project_id=${id}`),
        api.get(`/documents?project_id=${id}`),
        listFiles({ project_id: id }),
      ]);
      api.get("/people").then(({ data }) => setPeople(data)).catch((err) => console.error("Could not load people", err));
      setProject(p.data);
      setTasks(t.data);
      // Dedup folder markers by folder_key — a template accidentally applied twice can produce duplicates
      const rawFolders = (d.data || []).filter((x) => x.doc_type === "folder");
      const seenKeys = new Set();
      const uniqueFolders = [];
      for (const f of rawFolders) {
        const key = f.folder_key || f.id;
        if (!seenKeys.has(key)) { seenKeys.add(key); uniqueFolders.push(f); }
      }
      setFolders(uniqueFolders.sort((a, b) => (a.folder_order || 0) - (b.folder_order || 0)));
      setFiles(f);
    } catch (e) {
      toast.error("Could not load project");
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const stagesSorted = useMemo(
    () => [...(project?.stages || [])].sort((a, b) => a.order - b.order),
    [project?.stages]
  );

  // Tasks bucketed by stage_key (+ 'unassigned' bucket at end)
  const tasksByStage = useMemo(() => {
    const map = {};
    (stagesSorted || []).forEach((s) => { map[s.key] = []; });
    map._unassigned = [];
    tasks.forEach((t) => {
      const key = t.stage_key && map[t.stage_key] !== undefined ? t.stage_key : "_unassigned";
      map[key].push(t);
    });
    // Sort within each bucket: undone first, by priority then due
    const PRIO = { urgent: 0, high: 1, normal: 2, low: 3 };
    Object.keys(map).forEach((k) => {
      map[k].sort((a, b) => {
        const doneDiff = (a.status === "done" ? 1 : 0) - (b.status === "done" ? 1 : 0);
        if (doneDiff) return doneDiff;
        return (PRIO[a.priority] ?? 2) - (PRIO[b.priority] ?? 2);
      });
    });
    return map;
  }, [tasks, stagesSorted]);

  const activeStageIndex = useMemo(() => {
    return stagesSorted.findIndex((s) => s.key === project?.active_stage_key);
  }, [project?.active_stage_key, stagesSorted]);

  // Drag & drop — HTML5 native
  const handleDragStart = (task) => setDragTaskId(task.id);
  const handleDragEnd = () => { setDragTaskId(null); setDragOverStage(null); };
  const handleDrop = (e, toStageKey) => {
    e.preventDefault();
    setDragOverStage(null);
    if (!dragTaskId) return;
    const task = tasks.find((t) => t.id === dragTaskId);
    if (!task) return;
    const fromStage = task.stage_key || "_unassigned";
    if (fromStage === toStageKey) return;
    // Soft-gate: if moving *forward* to a stage past an incomplete earlier stage OR
    // moving into a stage while some tasks in earlier stages are still open — warn.
    const toIndex = stagesSorted.findIndex((s) => s.key === toStageKey);
    const fromIndex = stagesSorted.findIndex((s) => s.key === fromStage);
    let warn = null;
    if (toStageKey !== "_unassigned" && toIndex > fromIndex && toIndex > 0) {
      // Count open tasks in stages before the destination
      const stagesBefore = stagesSorted.slice(0, toIndex).map((s) => s.key);
      const blocking = tasks.filter((t) => stagesBefore.includes(t.stage_key || "") && t.status !== "done" && t.id !== task.id);
      if (blocking.length > 0) {
        warn = { blocking };
      }
    }
    if (warn) {
      setPendingMove({ task, fromStage, toStage: toStageKey, blocking: warn.blocking });
    } else {
      performMove(task, toStageKey === "_unassigned" ? null : toStageKey);
    }
  };

  const performMove = async (task, targetKey) => {
    // Optimistic
    setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, stage_key: targetKey } : t)));
    try {
      await api.put(`/tasks/${task.id}/stage`, { stage_key: targetKey });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Move failed");
      loadAll();
    }
  };

  const toggleTaskStatus = async (task) => {
    const nextStatus = task.status === "done" ? "todo" : "done";
    setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, status: nextStatus } : t)));
    try {
      await api.put(`/tasks/${task.id}`, { status: nextStatus });
    } catch {
      toast.error("Update failed");
      loadAll();
    }
  };

  const updateStageStatus = async (stageKey, nextStatus) => {
    try {
      const { data } = await api.put(`/projects/${id}/stages/${stageKey}`, { status: nextStatus });
      setProject((p) => ({ ...p, stages: data.stages, active_stage_key: data.active_stage_key }));
    } catch { toast.error("Stage update failed"); }
  };

  if (loading || !project) {
    return (
      <div className="flex items-center justify-center min-h-[50vh] text-muted-foreground gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading project…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="project-detail-page">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Link to="/projects" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mt-1">
          <ArrowLeft className="w-4 h-4" /> Projects
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Badge variant="secondary" className="text-[10px] font-normal">{project.type}</Badge>
            <Badge className={cn("text-[10px] font-normal", project.status === "active" ? "bg-[hsl(var(--halo-teal))]/15 text-[hsl(var(--halo-teal))] border-transparent" : "bg-muted text-muted-foreground border-transparent")}>{project.status}</Badge>
            {project.deadline && <span>Deadline {project.deadline}</span>}
            {project.budget && <span>· {money(project.budget)}</span>}
          </div>
          <h1 className="text-3xl font-semibold tracking-tight mt-1">{project.title}</h1>
          {project.description && <p className="text-sm text-muted-foreground mt-2 max-w-2xl">{project.description}</p>}
        </div>
      </div>

      {/* Stage rail */}
      {stagesSorted.length > 0 && (
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-1 text-xs" data-testid="stage-rail">
          {stagesSorted.map((s, i) => {
            const isActive = s.status === "active";
            const isDone = s.status === "done";
            return (
              <div key={s.key} className="flex items-center gap-1">
                <button
                  onClick={() => updateStageStatus(s.key, isDone ? "active" : "done")}
                  data-testid={`stage-rail-${s.key}`}
                  className={cn(
                    "px-3 py-1.5 rounded-full border transition-colors",
                    isDone && "bg-[hsl(var(--halo-teal))]/15 text-[hsl(var(--halo-teal))] border-transparent line-through",
                    isActive && "bg-[hsl(var(--studio-primary))]/15 text-[hsl(var(--studio-primary))] border-[hsl(var(--studio-primary)/0.30)] font-medium",
                    !isDone && !isActive && "text-muted-foreground border-border hover:bg-accent"
                  )}
                  title={isDone ? "Mark not done" : "Mark stage done"}
                >
                  {s.name}
                </button>
                {i < stagesSorted.length - 1 && <ChevronRight className="w-3 h-3 text-muted-foreground/40" />}
              </div>
            );
          })}
          </div>
          <div className="inline-flex rounded-lg border border-border p-0.5" data-testid="project-view-toggle">
            {[["kanban", "Kanban", Columns3], ["timeline", "Timeline", GanttChartSquare]].map(([key, label, Icon]) => (
              <button
                key={key}
                onClick={() => setView(key)}
                data-testid={`project-view-${key}`}
                className={cn(
                  "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs transition-colors",
                  view === key ? "bg-accent font-medium" : "text-muted-foreground hover:text-foreground"
                )}
              >
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Kanban of stages */}
      {stagesSorted.length > 0 && view === "timeline" ? (
        <ProjectTimeline project={project} stages={stagesSorted} tasks={tasks} />
      ) : stagesSorted.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="kanban">
          {stagesSorted.map((stage) => {
            const stageTasks = tasksByStage[stage.key] || [];
            const doneCount = stageTasks.filter((t) => t.status === "done").length;
            const isDragOver = dragOverStage === stage.key;
            return (
              <div
                key={stage.key}
                data-testid={`stage-column-${stage.key}`}
                onDragOver={(e) => { e.preventDefault(); setDragOverStage(stage.key); }}
                onDragLeave={() => setDragOverStage(null)}
                onDrop={(e) => handleDrop(e, stage.key)}
                className={cn(
                  "rounded-xl border p-3 min-h-[280px] transition-colors",
                  STAGE_STATUS_STYLE[stage.status] || STAGE_STATUS_STYLE.todo,
                  isDragOver && "ring-2 ring-[hsl(var(--studio-primary))] ring-offset-2 ring-offset-background"
                )}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{stage.name}</div>
                    <div className="text-[11px] text-muted-foreground">{doneCount}/{stageTasks.length} · {stage.status}</div>
                  </div>
                  {stage.target_duration_days && (
                    <div className="text-[10px] text-muted-foreground inline-flex items-center gap-1 shrink-0">
                      <Clock className="w-3 h-3" /> {stage.target_duration_days}d
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  {stageTasks.length === 0 && (
                    <div className="text-[11px] text-muted-foreground/60 italic px-2">Drop tasks here</div>
                  )}
                  {stageTasks.map((task) => (
                    <TaskCard
                      key={task.id}
                      task={task}
                      onToggle={() => toggleTaskStatus(task)}
                      onDragStart={() => handleDragStart(task)}
                      onDragEnd={handleDragEnd}
                      onOpen={() => setOpenTaskId(task.id)}
                      assignee={people.find((p) => p.id === task.assignee_person_id)}
                      onTaskChanged={(t) => setTasks((prev) => prev.map((x) => (x.id === t.id ? t : x)))}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <Card className="studio-card p-8 text-center text-sm text-muted-foreground">
          This project has no stages yet. Create one from a template to get the kanban.
        </Card>
      )}

      {/* Unassigned tasks — collapsible */}
      {tasksByStage._unassigned.length > 0 && (
        <div
          data-testid="stage-column-unassigned"
          onDragOver={(e) => { e.preventDefault(); setDragOverStage("_unassigned"); }}
          onDragLeave={() => setDragOverStage(null)}
          onDrop={(e) => handleDrop(e, "_unassigned")}
          className={cn("rounded-xl border border-dashed p-3", dragOverStage === "_unassigned" && "ring-2 ring-[hsl(var(--studio-primary))]/40")}
        >
          <div className="text-sm font-medium mb-3">Unassigned <span className="text-muted-foreground">({tasksByStage._unassigned.length})</span></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {tasksByStage._unassigned.map((t) => (
              <TaskCard key={t.id} task={t}
                onToggle={() => toggleTaskStatus(t)}
                onDragStart={() => handleDragStart(t)}
                onDragEnd={handleDragEnd}
                onOpen={() => setOpenTaskId(t.id)}
                assignee={people.find((p) => p.id === t.assignee_person_id)}
                onTaskChanged={(nt) => setTasks((prev) => prev.map((x) => (x.id === nt.id ? nt : x)))}
              />
            ))}
          </div>
        </div>
      )}

      {/* Folder browser + drop zone */}
      {folders.length > 0 && (
        <div>
          <div className="text-sm font-semibold mb-3">Folders</div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="folder-browser">
            {folders.map((fld) => {
              const inFolder = files.filter((f) => f.folder_key === fld.folder_key);
              return (
                <button
                  key={fld.id}
                  onClick={() => setOpenFolder(fld)}
                  data-testid={`folder-${fld.folder_key}`}
                  className="text-left p-4 rounded-xl border border-border hover:border-[hsl(var(--studio-primary)/0.4)] hover:bg-[hsl(var(--studio-primary)/0.03)] transition-colors"
                >
                  <Folder className="w-6 h-6 text-[hsl(var(--studio-primary))]" strokeWidth={1.75} />
                  <div className="mt-2 text-sm font-medium truncate">{fld.title}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{inFolder.length} file{inFolder.length === 1 ? "" : "s"}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="pt-4 border-t border-border">
        <div className="text-sm font-semibold mb-3">Attach files to this project</div>
        <FileDropZone
          context={{ project_id: id }}
          onUploaded={(rec) => setFiles((prev) => [rec, ...prev])}
          label="Drop files to attach to the project root"
        />
      </div>

      {/* All project files (non-foldered) */}
      {files.filter((f) => !f.folder_key).length > 0 && (
        <div>
          <div className="text-sm font-semibold mb-3">Project files</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {files.filter((f) => !f.folder_key).map((f) => (
              <FileRow key={f.id} file={f} onPreview={() => setPreviewFile(f)} onDelete={async () => {
                await deleteFile(f.id);
                setFiles((prev) => prev.filter((x) => x.id !== f.id));
              }}/>
            ))}
          </div>
        </div>
      )}

      {/* Folder detail dialog */}
      <FolderDialog
        folder={openFolder}
        files={files.filter((f) => openFolder && f.folder_key === openFolder.folder_key)}
        onClose={() => setOpenFolder(null)}
        onUploaded={(rec) => setFiles((prev) => [rec, ...prev])}
        onDelete={async (fileId) => {
          await deleteFile(fileId);
          setFiles((prev) => prev.filter((x) => x.id !== fileId));
        }}
        projectId={id}
        onPreview={(f) => setPreviewFile(f)}
      />

      <FilePreviewDialog file={previewFile} open={!!previewFile} onClose={() => setPreviewFile(null)} />

      {/* Task detail drawer */}
      <TaskDrawer
        taskId={openTaskId}
        open={!!openTaskId}
        onClose={() => setOpenTaskId(null)}
        stages={stagesSorted}
        onChanged={(t) => {
          if (t?._deleted) setTasks((prev) => prev.filter((x) => x.id !== t.id));
          else setTasks((prev) => prev.map((x) => (x.id === t.id ? t : x)));
        }}
      />

      {/* Soft-gate warning dialog */}
      <Dialog open={!!pendingMove} onOpenChange={(o) => { if (!o) setPendingMove(null); }}>
        <DialogContent data-testid="stage-move-warning">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-[hsl(var(--studio-warning))]" />
              Move task to a later stage?
            </DialogTitle>
          </DialogHeader>
          {pendingMove && (
            <div className="space-y-3 text-sm">
              <p>
                You're moving <b>{pendingMove.task.title}</b> forward, but there {pendingMove.blocking.length === 1 ? "is" : "are"}{" "}
                still <b>{pendingMove.blocking.length}</b> open task{pendingMove.blocking.length === 1 ? "" : "s"} in earlier stages.
              </p>
              <div className="rounded-md border border-border p-3 max-h-40 overflow-y-auto space-y-1">
                {pendingMove.blocking.slice(0, 6).map((t) => (
                  <div key={t.id} className="text-xs text-muted-foreground truncate">· {t.title}</div>
                ))}
                {pendingMove.blocking.length > 6 && <div className="text-xs text-muted-foreground">+ {pendingMove.blocking.length - 6} more…</div>}
              </div>
              <p className="text-xs text-muted-foreground">Mahano doesn't block you — this is just a heads-up. You can move it anyway.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingMove(null)} data-testid="stage-move-cancel">Cancel</Button>
            <Button
              onClick={() => {
                performMove(pendingMove.task, pendingMove.toStage === "_unassigned" ? null : pendingMove.toStage);
                setPendingMove(null);
              }}
              data-testid="stage-move-confirm"
            >Move anyway</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TaskCard({ task, onToggle, onDragStart, onDragEnd, onOpen, assignee, onTaskChanged }) {
  const isDone = task.status === "done";
  const subs = task.subtasks || [];
  const subsPending = subs.length > 0 && subs.some((s) => !s.done);
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      data-testid={`task-card-${task.id}`}
      className={cn(
        "group rounded-lg border border-border bg-card shadow-sm cursor-grab active:cursor-grabbing transition-all p-2.5",
        isDone && "opacity-60"
      )}
    >
      <div className="flex items-start gap-2">
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (!isDone && subsPending) { setExpanded(true); return; }
          onToggle();
        }}
        className="mt-0.5 shrink-0"
        data-testid={`task-toggle-${task.id}`}
      >
        {isDone
          ? <CheckCircle2 className="w-4 h-4 text-[hsl(var(--halo-teal))]" />
          : <Circle className="w-4 h-4 text-muted-foreground/50 hover:text-[hsl(var(--studio-primary))]" />}
      </button>
      <button
        type="button"
        onClick={onOpen}
        data-testid={`task-open-${task.id}`}
        className="flex-1 min-w-0 text-left"
      >
        <div className={cn("text-sm leading-tight", isDone && "line-through")}>{task.title}</div>
        <div className="flex items-center gap-1.5 mt-1.5 text-[10px]">
          {task.priority !== "normal" && (
            <span className={cn("px-1.5 py-0.5 rounded uppercase font-medium border", PRIORITY_STYLE[task.priority])}>{task.priority}</span>
          )}
          {task.due_date && <span className="text-muted-foreground">{task.due_date}</span>}
          {(subs.length > 0) && (
            <span className="text-muted-foreground">{subs.filter((s) => s.done).length}/{subs.length} subtasks</span>
          )}
          {task.is_recurring && (
            <span className="text-[hsl(var(--studio-primary))] inline-flex items-center gap-0.5" data-testid={`task-recurring-${task.id}`}>
              <Repeat className="w-2.5 h-2.5" /> {task.recurrence_rule || "weekly"}
            </span>
          )}
          {(task.comments?.length > 0) && (
            <span className="text-muted-foreground inline-flex items-center gap-0.5"><MessageSquare className="w-2.5 h-2.5" /> {task.comments.length}</span>
          )}
        </div>
      </button>
      <div className="flex flex-col items-end gap-1 shrink-0">
        <GripVertical className="w-3.5 h-3.5 text-muted-foreground/30 opacity-0 group-hover:opacity-100 mt-0.5" />
        <AssigneeAvatar person={assignee} />
      </div>
      </div>
      {subs.length > 0 && expanded && (
        <SubtaskChecklist task={task} onTaskChanged={onTaskChanged} />
      )}
    </div>
  );
}

function FileRow({ file, onDelete, onPreview }) {
  const download = async () => {
    try {
      const url = await fileBlobUrl(file.id);
      const a = document.createElement("a");
      a.href = url; a.download = file.original_filename; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch { toast.error("Download failed"); }
  };
  return (
    <Card className="studio-card p-3 flex items-center gap-3" data-testid={`project-file-${file.id}`}>
      <button onClick={onPreview} data-testid={`file-preview-${file.id}`} className="flex items-center gap-3 flex-1 min-w-0 text-left">
        <div className="w-10 h-10 rounded-lg bg-accent/60 flex items-center justify-center shrink-0">
          <Eye className="w-4 h-4 text-muted-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate" title={file.original_filename}>{file.original_filename}</div>
          <div className="text-[11px] text-muted-foreground">{humanBytes(file.size)} · {(file.content_type || "").split("/")[1] || "file"}</div>
        </div>
      </button>
      <Button variant="ghost" size="sm" onClick={download}><Download className="w-3.5 h-3.5"/></Button>
      <Button variant="ghost" size="sm" onClick={onDelete}><Trash2 className="w-3.5 h-3.5"/></Button>
    </Card>
  );
}

function FolderDialog({ folder, files, onClose, onUploaded, onDelete, projectId, onPreview }) {
  if (!folder) return null;
  return (
    <Dialog open={!!folder} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="folder-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderOpen className="w-5 h-5 text-[hsl(var(--studio-primary))]" />
            {folder.title}
          </DialogTitle>
        </DialogHeader>
        <FileDropZone
          context={{ project_id: projectId, folder_key: folder.folder_key }}
          onUploaded={onUploaded}
          label={`Drop files into "${folder.title}"`}
          data-testid="folder-dropzone"
        />
        <div className="mt-4 space-y-2 max-h-72 overflow-y-auto">
          {files.length === 0 && <div className="text-sm text-muted-foreground text-center py-6">No files in this folder yet.</div>}
          {files.map((f) => (
            <FileRow key={f.id} file={f} onDelete={() => onDelete(f.id)} onPreview={() => onPreview(f)} />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
